import React from 'react';
import { Text, View } from '@aws-amplify/ui-react';

const badge = (background, color) => ({
  background,
  color,
  padding: '2px 6px',
  borderRadius: '4px',
  whiteSpace: 'nowrap',
});

function lifecycleStyle(life) {
  if (life === 'ago') return badge('#e6f4ea', '#1e4620');
  if (life === 'external') return badge('#fff4e5', '#7a4a00');
  return badge('#eee', '#444');
}

function lifecycleLabel(life) {
  if (life === 'ago') return 'ago';
  if (life === 'external') return 'external';
  return '—';
}

function num(v) {
  if (v === undefined || v === null) return '—';
  return Number(v).toLocaleString();
}

/**
 * Per–Argyle-tenant summary shared by Gig Account Status and Disconnect Queue.
 * Orphans come from the last perioddata Redis snapshot (provider users with no
 * Ago person mapping).
 */
export default function GigTenantSummary({ tenants }) {
  if (!tenants?.length) return null;

  return (
    <View marginTop="1rem" marginBottom="0.5rem">
      <Text fontWeight="bold">Gig tenants</Text>
      <Text fontSize="0.85rem" color="#444" marginBottom="0.35rem">
        <strong>Argyle users</strong> and <strong>Connected accts</strong> are the
        Argyle-console analogues from the last perioddata run (enumerated provider
        users, and accounts with <code>connection.status = connected</code>) — not a
        live dashboard poll. <strong>On record (Ago)</strong> is our live{' '}
        <code>platform_accounts</code> bookkeeping. Orphans are provider users with no
        Ago person mapping. External tenants own Argyle user create/delete.
      </Text>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead>
          <tr>
            <th align="left">Tenant</th>
            <th align="left">Lifecycle</th>
            <th align="left">Fleets</th>
            <th align="right" title="Argyle Users card — from last perioddata enumeration">
              Argyle users
            </th>
            <th
              align="right"
              title="Argyle Connected card — accounts with connection.status=connected at last run"
            >
              Connected accts
            </th>
            <th align="right">Orphan users</th>
            <th align="right">Orphan accts</th>
            <th align="right">Dead</th>
            <th align="right" title="platform_accounts with provider_acct_removed_at IS NULL">
              On record (Ago)
            </th>
            <th align="right">Pending disc.</th>
            <th align="right">Pending repair</th>
          </tr>
        </thead>
        <tbody>
          {tenants.map((t) => (
            <tr key={t.jobApiInfoId}>
              <td>
                {t.jobApiInfoId}
                {t.comment ? (
                  <Text as="span" fontSize="0.8rem" color="#555">
                    {' '}
                    · {t.comment}
                  </Text>
                ) : null}
              </td>
              <td>
                <span style={lifecycleStyle(t.providerUserLifecycle)}>
                  {lifecycleLabel(t.providerUserLifecycle)}
                </span>
              </td>
              <td>
                {(t.fleets || []).map((f) => f.fleetCode).join(', ') || '—'}
              </td>
              <td align="right">{num(t.argyleUsers)}</td>
              <td align="right">{num(t.argyleConnectedAccounts)}</td>
              <td align="right">{num(t.orphanUsers)}</td>
              <td align="right">{num(t.orphanAccounts)}</td>
              <td align="right">{num(t.deadUsers)}</td>
              <td align="right">{num(t.onRecordAccounts)}</td>
              <td align="right">{num(t.pendingDisconnects)}</td>
              <td align="right">{num(t.pendingRepairs)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </View>
  );
}
